import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/context/LanguageContext";
import { SERVICES_SCHEMA } from "@/config/structuredData";
import { ServiceBookingOptions } from "@/components/ServiceBookingOptions";
import { STATIC_SERVICE_SEO } from "@/config/serviceSeo";
import { Link } from "@/i18n/LocalizedLink";
import { MapPin, Route, Landmark, Users, Clock, Briefcase, ArrowRight, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { QuoteButton, Eyebrow } from "@/components/shared";
import { IMG } from "@/config/images";
import { VehicleOptions } from "@/components/VehicleOptions";
import { getVehicleGuidance } from "@/config/locationVehicleGuidance";

const DETAIL = [
  { icon: MapPin, title: "Local city travel", points: ["Point-to-point drops within Satna and nearby towns", "Local hire: 120 KM / 12 hours plus fuel", "Shopping, events and family errands"] },
  { icon: Route, title: "Outstation — one-way & round trip", points: ["One-way drops where offered", "Round trips with waiting", "Multi-day outstation journeys"] },
  { icon: Clock, title: "Railway station & airport transfers", points: ["Timed station pickups and drops", "Airport transfers with prebooking", "Share your train/flight time for accurate timing"] },
  { icon: Landmark, title: "Pilgrimage & temple tours", points: ["Maihar, Chitrakoot and Khajuraho circuits", "Multi-stop darshan planning", "Comfortable vehicles with waiting time"] },
  { icon: Users, title: "Group travel", points: ["Tempo Travellers for group travel", "Weddings, reunions and tours", "Luggage-friendly planning"] },
  { icon: Briefcase, title: "Corporate & multi-day", points: ["Business travel and site visits", "Multi-city itineraries", "Planned, prebooked schedules"] },
];

export default function Services() {
  const { t } = useLanguage();
  return (
    <>
      <Seo
        title={t(STATIC_SERVICE_SEO.services.title)}
        description={t(STATIC_SERVICE_SEO.services.description)}
        jsonLd={SERVICES_SCHEMA}
        image={IMG.outstation}
      />
      <section className="relative overflow-hidden bg-[#071426]">
        <div className="absolute inset-0"><OptimizedImage src={IMG.outstation} sizes="100vw" fetchPriority="high" alt={t("Cars travelling on a highway representing outstation taxi services")} className="h-full w-full object-cover opacity-25" /><div className="absolute inset-0 bg-[#071426]/85" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Eyebrow className="text-amber-400">{t("Taxi Services")}</Eyebrow>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">{t(STATIC_SERVICE_SEO.services.h1)}</h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-300">{t("Explore taxi services for city trips, outstation travel, station transfers and pilgrimage journeys. Choose car hire for a smaller party or Tempo Traveller booking for a group; cab availability and the fare are confirmed for your route.")}</p>
          <div className="mt-6"><QuoteButton testId="services-hero-quote-button">{t("Request a Quote")}</QuoteButton></div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DETAIL.map((d) => (
            <div key={d.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`service-detail-${d.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><d.icon className="h-5 w-5" aria-hidden="true" /></span>
              <h2 className="mt-4 text-lg font-bold text-slate-900">{t(d.title)}</h2>
              <ul className="mt-3 space-y-2">
                {d.points.map((p) => <li key={p} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 text-emerald-600 shrink-0" aria-hidden="true" />{t(p)}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <ServiceBookingOptions page="services" className="mt-12" />
        <VehicleOptions city="Satna" localGuidance={t(getVehicleGuidance("/"))} className="mt-12" />
        <div className="mt-10 rounded-2xl bg-[#0B1E36] p-6 sm:p-8 text-center">
          <h2 className="text-xl font-bold text-white">{t("Not sure which service fits?")}</h2>
          <p className="mt-2 text-slate-300">{t("Tell us your trip and we'll suggest the best option and vehicle.")}</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <QuoteButton testId="services-cta-quote-button">{t("Request a Quote")}</QuoteButton>
            <Link to="/tempo-traveller/" className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-5 py-3 font-semibold text-slate-200 hover:bg-white/5" data-testid="services-tempo-link">{t("Group travel by Tempo Traveller")} <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
